import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Award, Building2, GraduationCap, Users, Hospital,
  Sparkles, ArrowRight, Check, PhoneCall, Mail, MapPin, MessageCircle,
  ChevronDown, Stethoscope, HeartPulse, ScrollText, ShoppingCart,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Logo } from "@/components/Logo";
import { LANDING } from "@/constants/testIds";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useSitePageVisibility } from "@/context/SitePageVisibilityContext";
import { useSiteConfig, useSupportPhone, useWhatsAppUrl } from "@/context/SiteConfigContext";
import { InquiryForm } from "@/components/InquiryForm";
import { FeaturedCoursesSection } from "@/pages/storefront/StorefrontPages";
import { api } from "@/lib/api";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const CATEGORIES = [
  { label: "Nursing & Patient Care", icon: HeartPulse },
  { label: "Hospital Administration", icon: Building2 },
  { label: "Infection Control (NABH)", icon: ShieldCheck },
  { label: "Emergency & Critical Care", icon: Stethoscope },
  { label: "Diagnostics & Laboratory", icon: ScrollText },
  { label: "Healthcare Quality & Compliance", icon: Award },
];

const FEATURES = [
  { icon: Users, title: "Multi-tenant for Hospitals", body: "Onboard hospitals, colleges, and diagnostic centres as isolated tenants with branding and role-based access." },
  { icon: GraduationCap, title: "Course Mapping", body: "Assign specific courses to specific institutes and departments — no fluff, only what your staff needs." },
  { icon: ShieldCheck, title: "Compliance-first", body: "NABH, JCI, and NABL-ready reporting. Audit-grade evidence at the click of a button." },
  { icon: Award, title: "Certificates + QR", body: "Tamper-proof certificates with QR verification, expiry alerts, and renewal workflows." },
];

const FAQS = [
  { q: "Who is Steward Learning Cloud built for?", a: "Hospitals, nursing colleges, diagnostic labs, and healthcare training institutes that need auditable, role-based training for clinical and non-clinical staff." },
  { q: "Can we get our own branded portal?", a: "Yes. White-label branding, custom domains and certificate design will be enabled in a later phase — the architecture is ready today." },
  { q: "Is the platform NABH / JCI aligned?", a: "The compliance dashboard and reports are designed around common healthcare accreditation frameworks including NABH, JCI and NABL." },
  { q: "Do you offer mobile apps?", a: "Android and iOS apps will be published from Phase 14 onwards using our shared API foundation." },
];

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3ODMxNjY1MzV8MA&ixlib=rb-4.1.0&q=85";
const HOSPITAL_IMAGE =
  "https://images.unsplash.com/photo-1712215544003-af10130f8eb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxkb2N0b3IlMjBtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3ODMxNjY1MzV8MA&ixlib=rb-4.1.0&q=85";
const LEARNER_IMAGE =
  "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwc3R1ZGVudHN8ZW58MHx8fHwxNzgzMTY2NTM1fDA&ixlib=rb-4.1.0&q=85";

function Header() {
  const [open, setOpen] = useState(false);
  const { pages } = useSitePageVisibility();
  const isOn = (k) => {
    const v = pages[k];
    if (v === undefined) return true;
    if (typeof v === "boolean") return v;
    return !!(v.show_in_header && v.page_active);
  };
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:h-20 md:px-6">
        <Link to="/"><Logo /></Link>
        <nav className="hidden items-center gap-8 text-sm font-medium text-slate-600 md:flex">
          {isOn("courses") && <Link to="/courses" className="hover:text-[#101876] font-semibold" data-testid="landing-nav-courses">Courses</Link>}
          {isOn("section_why") && <a href="#why" className="hover:text-[#101876]">Why Steward</a>}
          {isOn("section_training") && <a href="#categories" className="hover:text-[#101876]">Training</a>}
          {isOn("for_hospitals") && <Link to="/for-hospitals" className="hover:text-[#101876]" data-testid="landing-nav-hospitals">For Hospitals</Link>}
          {isOn("section_faq") && <a href="#faq" className="hover:text-[#101876]">FAQ</a>}
          {isOn("section_contact") && <a href="#contact" className="hover:text-[#101876]">Contact</a>}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/cart" data-testid="landing-nav-cart"
                className="relative rounded-full border border-slate-200 p-2 hover:bg-slate-50">
            <ShoppingCart className="h-4 w-4 text-slate-700" />
          </Link>
          <Link
            to="/login"
            className="hidden rounded-md px-4 py-2 text-sm font-semibold text-[#101876] hover:bg-slate-100 md:inline-flex"
            data-testid={LANDING.navLogin}
          >
            Log in
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0c135c]"
            data-testid={LANDING.navGetStarted}
          >
            Get Started <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="ml-1 inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-600 md:hidden"
            aria-label="Toggle menu"
            data-testid="landing-mobile-menu"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-4 md:hidden">
          <div className="grid gap-2 text-sm font-medium text-slate-700">
            {isOn("section_why") && <a href="#why">Why Steward</a>}
            {isOn("section_training") && <a href="#categories">Training</a>}
            {isOn("for_hospitals") && <Link to="/for-hospitals" data-testid="landing-mobile-nav-hospitals">For Hospitals</Link>}
            {isOn("section_faq") && <a href="#faq">FAQ</a>}
            {isOn("section_contact") && <a href="#contact">Contact</a>}
            <Link to="/login" className="mt-2 inline-flex rounded-md bg-slate-100 px-4 py-2 font-semibold text-[#101876]">Log in</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="hero-grid absolute inset-0 opacity-70" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 md:px-6 md:py-24 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#101876]/20 bg-[#101876]/5 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-[#101876]"
          >
            <Sparkles className="h-3.5 w-3.5" /> Healthcare Learning · Compliance · Competency
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="mt-6 text-4xl font-black tracking-tight text-[#101876] sm:text-5xl lg:text-6xl"
          >
            The training platform<br/>
            <span className="text-slate-900">purpose-built for </span>
            <span className="relative inline-block">
              <span className="text-[#329112]">healthcare.</span>
              <span className="absolute -bottom-1 left-0 h-1 w-full rounded-full bg-[#329112]/30" />
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg"
          >
            Roll out clinical training, compliance modules and certification programs to
            hospitals, nursing colleges and diagnostic centres — all from one auditable,
            role-based cloud. Delivered by <span className="font-semibold text-slate-900">SIHMT</span>.
          </motion.p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0c135c]"
              data-testid={LANDING.ctaLogin}
            >
              Login <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 rounded-md bg-[#329112] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#256b0d]"
              data-testid={LANDING.ctaSignup}
            >
              Request Demo
            </Link>
            <a
              href="#categories"
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]"
              data-testid={LANDING.ctaExplore}
            >
              Explore Courses
            </a>
          </div>
          <div className="mt-10 grid max-w-md grid-cols-3 gap-6">
            {[
              { k: "500+", v: "Hospitals ready" },
              { k: "50k+", v: "Learners target" },
              { k: "NABH", v: "Aligned" },
            ].map((s) => (
              <div key={s.k}>
                <div className="text-2xl font-black text-[#101876]">{s.k}</div>
                <div className="text-xs uppercase tracking-widest text-slate-500">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative lg:col-span-5"
          data-testid="hero-inquiry"
        >
          <div className="absolute -left-6 -top-6 hidden h-20 w-20 rounded-2xl bg-[#329112]/15 md:block" />
          <div className="absolute -bottom-6 -right-4 hidden h-16 w-16 rounded-2xl bg-[#101876]/10 md:block" />
          <div className="relative">
            <InquiryForm variant="hero" sourcePage="landing:hero" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section id="about" className="border-y border-slate-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">About</div>
            <h2 className="mt-2 text-3xl font-black text-slate-900 sm:text-4xl">Built by Steward Healthcare. Delivered by SIHMT.</h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base leading-relaxed text-slate-600 md:text-lg">
              Steward Learning Cloud is the flagship digital training platform from
              <span className="font-semibold text-slate-900"> Steward Healthcare India Pvt. Ltd.</span>, powered by the
              <span className="font-semibold text-slate-900"> Steward Institute of Healthcare Management & Technology (SIHMT)</span>.
              We combine hospital consulting expertise with modern SaaS engineering
              to give every hospital, college and lab a training system that actually gets used.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section id="why" className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
      <div className="max-w-2xl">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">Why Steward</div>
        <h2 className="mt-2 text-3xl font-black text-slate-900 sm:text-4xl">A learning cloud that speaks healthcare.</h2>
        <p className="mt-4 text-base text-slate-600">
          Every module, report and workflow is designed with hospital operations in mind — from
          bedside nursing skill drills to accreditation-day evidence packs.
        </p>
      </div>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className="group rounded-2xl border border-slate-200 bg-white p-6 transition hover:-translate-y-1 hover:border-[#101876]/40 hover:shadow-lg"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#101876]/5 text-[#101876] transition group-hover:bg-[#101876] group-hover:text-white">
              <f.icon className="h-5 w-5" />
            </div>
            <h3 className="mt-4 text-lg font-bold text-slate-900">{f.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.body}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function CategoriesSection() {
  return (
    <section id="categories" className="bg-white">
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">Training Categories</div>
            <h2 className="mt-2 text-3xl font-black text-slate-900 sm:text-4xl">Ready-to-deploy clinical & compliance tracks.</h2>
          </div>
          <span className="text-xs uppercase tracking-widest text-slate-500">6 core streams · expandable</span>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((c, i) => (
            <div
              key={c.label}
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50/50 p-5 transition hover:-translate-y-0.5 hover:border-[#329112]/40 hover:bg-white"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#329112]/10 text-[#329112]">
                <c.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-base font-bold text-slate-900">{c.label}</div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Track {String(i + 1).padStart(2, "0")}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DualAudienceSection() {
  return (
    <section id="for-hospitals" className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white">
          <img src={HOSPITAL_IMAGE} alt="Hospital" className="h-64 w-full object-cover" />
          <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-t from-white to-transparent" />
          <div className="relative -mt-16 px-8 pb-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">For Hospitals & Institutes</div>
            <h3 className="mt-2 text-2xl font-black text-slate-900">Train, certify, and prove compliance.</h3>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              {["Multi-tenant, white-label ready portals","Course mapping per department","Certificate expiry & renewal automation","Real-time compliance dashboard"].map((x) => (
                <li key={x} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 text-[#329112]" /> {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white">
          <img src={LEARNER_IMAGE} alt="Learner" className="h-64 w-full object-cover" />
          <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-t from-white to-transparent" />
          <div className="relative -mt-16 px-8 pb-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">For Individual Learners</div>
            <h3 className="mt-2 text-2xl font-black text-slate-900">Grow your healthcare career.</h3>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              {["Self-paced clinical & admin tracks","Random-question adaptive exams","Downloadable QR-verified certificates","Career-ready CE credits"].map((x) => (
                <li key={x} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 text-[#329112]" /> {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function LogoRow({ eyebrow, heading, logos }) {
  if (!logos || logos.length === 0) return null;
  return (
    <div data-testid={`homepage-logo-row-${eyebrow.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-center text-xs font-bold uppercase tracking-[0.3em] text-slate-500">
        {eyebrow}
      </div>
      {heading && (
        <h3 className="mt-2 text-center text-lg font-black text-slate-800 md:text-xl">{heading}</h3>
      )}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
        {logos.map((l) => {
          const img = (
            <img
              src={l.logo_url}
              alt={l.name}
              title={l.name}
              className="h-14 w-auto max-w-[180px] object-contain grayscale opacity-80 transition hover:grayscale-0 hover:opacity-100"
              loading="lazy"
              data-testid={`homepage-logo-${l.name.replace(/\s+/g, "-").toLowerCase()}`}
            />
          );
          return l.link_url ? (
            <a key={l.name + l.logo_url} href={l.link_url} target="_blank" rel="noreferrer">{img}</a>
          ) : (
            <div key={l.name + l.logo_url}>{img}</div>
          );
        })}
      </div>
    </div>
  );
}

function OffersStrip() {
  const [coupons, setCoupons] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/public/coupons/homepage");
        if (alive) setCoupons(data.data?.coupons || []);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);
  if (coupons.length === 0) return null;

  const copy = (code) => {
    navigator.clipboard.writeText(code).catch(() => {});
    // dynamic import to avoid pulling sonner into main chunk of landing
    import("sonner").then(({ toast }) => toast.success(`Coupon code ${code} copied successfully.`));
  };

  return (
    <section className="border-y border-slate-200 bg-[#F8FAFC]" data-testid="landing-offers">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="text-center">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#329112]">Available Offers</div>
          <h2 className="mt-2 text-3xl font-black text-slate-900 md:text-4xl">Apply coupon at checkout and save.</h2>
          <p className="mt-2 text-sm text-slate-600">Codes below are live — copy one and use it on the cart page.</p>
        </div>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {coupons.map((c) => (
            <div key={c.id} data-testid={`offer-${c.coupon_code}`}
              className="group relative overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-white p-5 transition hover:border-[#329112] hover:shadow-lg">
              {c.coupon_badge_text && (
                <div className="absolute right-4 top-4 rounded-full bg-[#329112]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#329112]">
                  {c.coupon_badge_text}
                </div>
              )}
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Coupon code</div>
              <div className="mt-1 font-mono text-2xl font-black text-[#101876]">{c.coupon_code}</div>
              <div className="mt-2 text-sm font-bold text-slate-900">
                {c.discount_type === "percentage"
                  ? `Save ${c.discount_value}%${c.max_discount_amount ? ` (up to ₹${c.max_discount_amount})` : ""}`
                  : `Flat ₹${c.discount_value} off`}
              </div>
              {c.description && <p className="mt-1 text-xs text-slate-600 leading-relaxed">{c.description}</p>}
              {c.valid_till && (
                <div className="mt-2 text-[11px] text-slate-500">
                  Valid till {new Date(c.valid_till).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                </div>
              )}
              {c.minimum_order_amount > 0 && (
                <div className="text-[11px] text-slate-500">Min order ₹{c.minimum_order_amount}</div>
              )}
              <div className="mt-3 flex gap-2">
                <button onClick={() => copy(c.coupon_code)}
                  className="flex-1 rounded-lg bg-[#101876] px-3 py-2 text-xs font-bold text-white hover:bg-[#0d1360]"
                  data-testid={`offer-copy-${c.coupon_code}`}>
                  Copy code
                </button>
                <Link to="/courses"
                  className="rounded-lg border border-[#101876] px-3 py-2 text-xs font-bold text-[#101876] hover:bg-[#101876]/5">
                  View courses
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


function PartnersStrip() {
  const [partners, setPartners] = useState([]);
  const [affiliations, setAffiliations] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, a] = await Promise.all([
          api.get("/public/homepage/partners"),
          api.get("/public/homepage/affiliation-partners"),
        ]);
        if (!alive) return;
        setPartners(p.data?.data?.logos || []);
        setAffiliations(a.data?.data?.logos || []);
      } catch { /* silent — section simply hides if API fails */ }
    })();
    return () => { alive = false; };
  }, []);

  if (partners.length === 0 && affiliations.length === 0) return null;

  return (
    <section className="border-y border-slate-200 bg-white" data-testid="homepage-partners-strip">
      <div className="mx-auto max-w-7xl space-y-14 px-4 py-14 md:px-6 md:py-16">
        <LogoRow
          eyebrow="Our Partner"
          logos={partners}
        />
        <LogoRow
          eyebrow="Our Affiliation Partner"
          logos={affiliations}
        />
      </div>
    </section>
  );
}

function TestimonialSection() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-[#101876] to-[#0c135c] p-10 text-white md:p-16">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-white/70">What partners are saying</div>
          <blockquote className="mt-4 text-2xl font-bold leading-snug md:text-3xl">
            "For the first time we can prove — in one click — that every nurse in every unit
            has completed their infection-control refresher this quarter. Steward gave us
            our audit weekend back."
          </blockquote>
          <div className="mt-6 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-[#329112]" />
            <div>
              <div className="text-sm font-bold">Dr. Ananya Menon</div>
              <div className="text-xs text-white/70">Chief Quality Officer · Referenced multi-specialty hospital</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section id="faq" className="bg-white">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 md:px-6 md:py-20 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">FAQ</div>
          <h2 className="mt-2 text-3xl font-black text-slate-900 sm:text-4xl">Common questions.</h2>
          <p className="mt-4 text-sm text-slate-600">
            Can't find what you're looking for? Reach out via the contact section below.
          </p>
        </div>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-slate-200">
                <AccordionTrigger className="text-left text-base font-semibold text-slate-900">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section id="contact" className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
      <div className="rounded-3xl border border-slate-200 bg-white p-8 md:p-12">
        <div className="grid gap-10 md:grid-cols-3">
          <div className="md:col-span-1">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">Contact</div>
            <h2 className="mt-2 text-2xl font-black text-slate-900">Talk to us.</h2>
            <p className="mt-3 text-sm text-slate-600">
              Ready to roll out training across your organization? Our team will help you plan a phased rollout.
            </p>
          </div>
          <div className="md:col-span-2 grid gap-4 sm:grid-cols-3">
            {[
              { icon: PhoneCall, title: "Call", value: "+91 96162-98162" },
              { icon: Mail, title: "Email", value: "info@stewardindia.com" },
              { icon: MapPin, title: "HQ", value: "2nd Floor, SCO 23, Hi Street, Eldeco Estate One, Panipat-132103" },
            ].map((c) => (
              <div key={c.title} className="rounded-2xl border border-slate-200 p-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#101876]/5 text-[#101876]">
                  <c.icon className="h-5 w-5" />
                </div>
                <div className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">{c.title}</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">{c.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 md:px-6 md:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-4 text-sm text-slate-600">
            Healthcare Learning. Compliance. Competency. Delivered digitally.
          </p>
          <div className="mt-4 space-y-1 text-xs text-slate-600" data-testid="footer-contact-legacy" hidden></div>
          <ContactCallLine />
        </div>
        {[
          { title: "Platform", links: [
            { label: "About", href: "#why" },
            { label: "Courses", href: "/courses" },
            { label: "For Hospitals", href: "/for-hospitals" },
            { label: "For Learners", href: "/courses" },
          ] },
          { title: "Company", links: [
            { label: "Contact", href: "#contact" },
            { label: "Verify Certificate", href: "/verify-certificate" },
            { label: "Partners", href: "#" },
            { label: "Careers", href: "#" },
          ] },
          { title: "Legal", links: [
            { label: "Privacy Policy",     href: "/legal/privacy-policy" },
            { label: "Terms & Conditions", href: "/legal/terms-conditions" },
            { label: "Refund Policy",      href: "/legal/refund-policy" },
            { label: "Cookies Policy",     href: "/legal/cookies-policy" },
          ] },
        ].map((col) => (
          <div key={col.title}>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{col.title}</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {col.links.map((l) => (
                <li key={l.label}>
                  {l.href.startsWith("/") ? (
                    <Link to={l.href} className="hover:text-[#101876]" data-testid={`footer-link-${l.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                      {l.label}
                    </Link>
                  ) : (
                    <a href={l.href} className="hover:text-[#101876]">{l.label}</a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-200 py-5">
        <div className="mx-auto flex max-w-7xl flex-col-reverse items-center justify-between gap-4 px-4 md:flex-row md:px-6">
          <div className="text-center text-xs text-slate-500 md:text-left">
            © {new Date().getFullYear()} Steward Healthcare India Pvt. Ltd. · SIHMT · All rights reserved.
          </div>
          <a
            href="https://stewardindia.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm transition hover:-translate-y-0.5 hover:border-[#101876]/40 hover:shadow-md"
            data-testid="footer-steward-website-link"
            title="Visit stewardindia.com — Main Steward Website"
          >
            <img
              src="/steward-healthcare-logo.png"
              alt="Steward Healthcare"
              className="h-10 w-auto max-w-[8rem] object-contain shrink-0"
              draggable={false}
            />
            <span className="flex flex-col leading-tight">
              <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500 group-hover:text-[#329112]">Main Steward Website</span>
              <span className="text-[10px] font-semibold text-slate-500">stewardindia.com →</span>
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
}

function ContactCallLine() {
  const { config } = useSiteConfig();
  const { display, telHref } = useSupportPhone();
  const email = config.support_email || "info@stewardindia.com";
  return (
    <div className="mt-4 space-y-1 text-xs text-slate-600" data-testid="footer-contact">
      <div><span className="font-semibold text-slate-800">Address:</span> 2nd Floor, SCO 23, Hi Street, Eldeco Estate One, Panipat-132103, Haryana, India</div>
      <div><span className="font-semibold text-slate-800">Email:</span> <a href={`mailto:${email}`} className="hover:text-[#101876]">{email}</a></div>
      <div><span className="font-semibold text-slate-800">Call:</span> <a href={telHref} className="hover:text-[#101876]">{display}</a></div>
    </div>
  );
}

function WhatsAppFab() {
  const url = useWhatsAppUrl();
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="fixed bottom-24 right-6 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-emerald-500/30 transition hover:-translate-y-1 lg:bottom-6"
      aria-label="Chat on WhatsApp"
      data-testid={LANDING.whatsappBtn}
    >
      <MessageCircle className="h-6 w-6" />
    </a>
  );
}

export default function LandingPage() {
  usePageTitle("Healthcare Learning · Compliance · Competency");
  const { hash } = useLocation();
  const { pages } = useSitePageVisibility();
  // Sections use only the header flag — they aren't real routes so
  // `page_active` gates the "visible on landing page" behavior too.
  const isOn = (k) => {
    const v = pages[k];
    if (v === undefined) return true;
    if (typeof v === "boolean") return v;
    return !!(v.page_active && v.show_in_header);
  };
  // Scroll to a section anchor when arriving from another page (e.g. clicking
  // "FAQ" in the storefront header lands on `/#faq`). Small timeout gives
  // section components (some render lazily / with animations) time to mount.
  useEffect(() => {
    if (!hash) return undefined;
    const id = hash.slice(1);
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [hash]);
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid={LANDING.root}>
      <Header />
      <Hero />
      {isOn("section_about") && <AboutSection />}
      {isOn("courses") && <FeaturedCoursesSection />}
      <OffersStrip />
      {isOn("section_why") && <WhySection />}
      {isOn("section_training") && <CategoriesSection />}
      <DualAudienceSection />
      <PartnersStrip />
      <TestimonialSection />
      {isOn("section_faq") && <FAQSection />}
      {isOn("section_contact") && <ContactSection />}
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
